import {
  ActionName,
  CategoryName,
  ConditionSetItemsP,
  DomainName,
  ExecutedActionP,
  ISelectInputControl,
  TControlName,
  tg,
} from '@infotech-soe/metaschema';

import { SIGN_ACTION_TYPE } from '../../../../helpers/constants';
import { TActionArgsValues } from '../../../../infrastructure/providers/abstract/IApiProvider';
import CachedSchemaRepository from '../../../../infrastructure/schemas/storage/CachedSchemaRepository';
import ISchemaRepository from '../../../../infrastructure/schemas/storage/ISchemaRepository';
import { NamedDomain } from '../../../../infrastructure/schemas/storage/NamedDomain';
import IIntlService from '../../../intl/IIntlService';
import IntlService from '../../../intl/IntlService';
import { isConditionForm, isPredefinedForm } from '../../typeguards';
import {
  FormControlViewModelType,
  LocalizedProps,
  SelectFormControlEntry,
  SelectFormControlEntryType,
  SelectFormControlViewModel,
} from '../../view-model/FormControlViewModel';
import {
  CategoryFormSource,
  FormSourceType,
} from '../../view-model/FormSource';
import { FormViewModel } from '../../view-model/FormViewModel';

class BuildSelectControl {
  private readonly schemaRepository: ISchemaRepository;
  private readonly intlService: IIntlService;

  constructor() {
    this.schemaRepository = new CachedSchemaRepository();
    this.intlService = new IntlService();
  }

  public async build(
    viewModel: FormViewModel,
    item: ISelectInputControl,
    localizedProps: LocalizedProps,
    args: TActionArgsValues,
    domains: Array<{ name: TControlName; domain: DomainName }>
  ): Promise<SelectFormControlViewModel> {
    const conditions = isConditionForm(viewModel.source)
      ? viewModel.source.conditions
      : undefined;

    const action =
      isConditionForm(viewModel.source) || isPredefinedForm(viewModel.source)
        ? undefined
        : viewModel.source.action;

    let options: SelectFormControlEntry[] = [];

    const domain = domains.find(d => d.name === item.name);

    if (domain) {
      const domainSchema = await this.schemaRepository.getDomain(domain.domain);
      options = await this.buildOptions(domainSchema, domain.domain);
    } else {
      options = await this.getSelectOptionsByDomainAndField(
        viewModel.source.category,
        item,
        action,
        conditions
      );
    }

    let value;

    if (
      item.storeValue &&
      !isPredefinedForm(viewModel.source) &&
      viewModel.source.formConfig[item.name] &&
      tg.isSomething(viewModel.source.formConfig[item.name].value) &&
      options.some(
        o =>
          o.type === SelectFormControlEntryType.OPTION &&
          o.value ===
            (viewModel.source as CategoryFormSource).formConfig[item.name].value
      )
    ) {
      value = viewModel.source.formConfig[item.name].value;
    } else {
      value = args[item.name];
    }

    return {
      columns: item.columns,
      noLabel: item.noLabel,
      type: FormControlViewModelType.SELECT_INPUT,
      name: item.name,
      labelWidth: item.labelWidth,
      options,
      value: value ?? null,
      disabled: item.disabled,
      required: item.required,
      storeValue: item.storeValue,
      readonly:
        item.readonly || viewModel.source.type === FormSourceType.REVIEW,
      hasValue: true,
      hasHistory: false,
      visible: item.visible,
      addAll: isConditionForm(viewModel.source) && !item.hideOptionAll,
      ...localizedProps,
    };
  }

  private async getSelectOptionsByDomainAndField(
    category: CategoryName,
    item: ISelectInputControl,
    action?: string,
    conditions?: ConditionSetItemsP
  ): Promise<SelectFormControlEntry[]> {
    let domain: DomainName | undefined;
    if (action) {
      domain = await this.extractDomainFromAction(category, action, item);
    } else if (conditions) {
      domain = conditions[item.name].domain;
    }

    if (!domain) {
      throw new Error(`${item.name} control lacks domain`);
    }

    const domainSchema = await this.schemaRepository.getDomain(domain);

    return this.buildOptions(domainSchema, domain);
  }

  private async extractDomainFromAction(
    category: CategoryName,
    action: ActionName,
    item: ISelectInputControl
  ): Promise<DomainName | undefined> {
    const actionSchema = (await this.schemaRepository.getAction(
      category,
      action
    )) as ExecutedActionP;

    if (actionSchema.Type === SIGN_ACTION_TYPE) {
      return;
    }

    const arg = actionSchema.Args[item.name] ?? {};

    if (tg.hasDomainProp(arg)) {
      return arg.domain;
    } else if (tg.hasCategoryFieldPath(arg)) {
      return (await this.schemaRepository.getField(category, arg.field)).domain;
    }
  }

  private async buildOptions(
    domainSchema: NamedDomain,
    domain: DomainName
  ): Promise<SelectFormControlEntry[]> {
    const options: SelectFormControlEntry[] = [];

    if (domainSchema.type === 'enum' && domainSchema.values) {
      for (const value of domainSchema.values) {
        options.push({
          type: SelectFormControlEntryType.OPTION,
          label: await this.intlService.getDomainEnumValue(domain, value),
          value,
        });
      }
    }

    return options;
  }
}

export default BuildSelectControl;
